/**
 * Multicolumn with video
 * -----------------------------------------------------------------------------
 * <mc-video> holds one hosted video inside a Multicolumn column.
 *
 * data-behavior="autoplay" — muted, looping, starts when the column reaches the
 *   middle of the screen, pauses when it leaves.
 * data-behavior="click"    — stays on its cover until clicked, then plays with
 *   sound.
 */
if (!customElements.get('mc-video')) {
  class McVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.autoplay = this.dataset.behavior !== 'click';
      this.linked = this.closest('a') !== null;
      this.reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      // Required for inline autoplay on iOS.
      this.video.muted = true;
      this.video.setAttribute('muted', '');
      this.video.playsInline = true;
      this.video.setAttribute('playsinline', '');

      this.video.addEventListener('play', () => this.setState(false));
      this.video.addEventListener('pause', () => this.setState(true));
      this.setState(true);

      this.bindControls();
      this.observe();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setState(paused) {
      this.classList.toggle('is-paused', paused);
      this.classList.toggle('is-playing', !paused);
    }

    /* ------------------------------------------------------------ playback - */

    play() {
      const video = this.video;

      // Bumping preload starts the download — calling load() here would abort
      // the play() that follows it.
      if (video.preload === 'none') video.preload = 'auto';

      const promise = video.play();
      if (!promise || typeof promise.catch !== 'function') return;

      promise.catch(() => {
        this.setState(true);

        if (video.dataset.playRetried) return;
        video.dataset.playRetried = 'true';

        video.addEventListener(
          'canplay',
          () => {
            if (!this.canAutoplay()) return;
            const retry = video.play();
            if (retry && typeof retry.catch === 'function') {
              retry.catch(() => this.setState(true));
            }
          },
          { once: true }
        );
      });
    }

    pause() {
      if (!this.video.paused) this.video.pause();
    }

    canAutoplay() {
      return (
        this.autoplay &&
        !this.reduceMotion &&
        !this.video.dataset.userPaused &&
        this.onScreen
      );
    }

    /* ------------------------------------------------------------ viewport - */

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.onScreen = true;
        if (this.canAutoplay()) this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.onScreen = entry.isIntersecting;

            if (entry.isIntersecting) {
              if (this.canAutoplay()) this.play();
            } else {
              this.pause();
              if (!this.video.muted) this.setMuted(true);
            }
          });
        },
        // Overlapping the middle 70% of the screen counts as "on screen". An
        // area threshold would misfire on columns taller than the viewport.
        { threshold: 0, rootMargin: '-15% 0px -15% 0px' }
      );

      this.observer.observe(this);
    }

    /* ------------------------------------------------------------ controls - */

    bindControls() {
      const sound = this.querySelector('.multicolumn-video__sound');

      // A column that links somewhere keeps the link as its only action.
      if (!this.linked) {
        this.addEventListener('click', (event) => {
          if (event.target.closest('.multicolumn-video__sound')) return;
          this.toggle();
        });
      }

      if (sound) {
        sound.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const unmute = this.video.muted;
          if (unmute) {
            // Only one video in these sections plays out loud at a time.
            document.querySelectorAll('mc-video').forEach((other) => {
              if (other !== this && other.setMuted) other.setMuted(true);
            });
          }

          this.setMuted(!unmute);
          if (unmute && this.video.paused) this.play();
        });
      }
    }

    toggle() {
      if (this.video.paused) {
        delete this.video.dataset.userPaused;
        // Click-to-play means the shopper asked for it — give them sound.
        if (!this.autoplay && this.video.muted) {
          document.querySelectorAll('mc-video').forEach((other) => {
            if (other !== this && other.setMuted) other.setMuted(true);
          });
          this.setMuted(false);
        }
        this.play();
      } else {
        this.video.dataset.userPaused = 'true';
        this.pause();
      }
    }

    setMuted(muted) {
      this.video.muted = muted;
      const button = this.querySelector('.multicolumn-video__sound');
      if (button) button.setAttribute('aria-pressed', muted ? 'false' : 'true');
    }
  }

  customElements.define('mc-video', McVideo);
}
